import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import {
  AddDeviceRequest,
  InstallationImp,
  InstallationsService,
  Timezone,
} from '@ubie/core';
import { FeedbackService, FormHelper } from '@ubie/shared';

@Component({
  selector: 'acl-add-device-modal',
  standalone: false,
  templateUrl: './add-device-modal.component.html',
  styleUrl: './add-device-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddDeviceModalComponent implements OnInit {
  private readonly modalController = inject(ModalController);
  private readonly formBuilder = inject(FormBuilder);
  private readonly installationsService = inject(InstallationsService);
  private readonly feedbackService = inject(FeedbackService);

  readonly _installationId = signal(0);
  readonly _timezoneData = signal<Timezone[]>([]);
  readonly _installation = signal<InstallationImp>({} as InstallationImp);

  @Input({ required: true }) set installationId(value: number) {
    this._installationId.set(value);
  }
  @Input({ required: true }) set timezoneData(value: Timezone[]) {
    this._timezoneData.set(value);
  }
  @Input({ required: true }) set installation(value: InstallationImp) {
    this._installation.set(value);
  }

  readonly selectedTimezone = signal<Timezone | undefined>(undefined);

  readonly deviceForm = this.formBuilder.group({
    serial_number: ['', Validators.required],
    activation_code: ['', Validators.required],
    timezone: [this.selectedTimezone(), Validators.required],
  });

  readonly formHelper = signal(new FormHelper(this.deviceForm));

  readonly isFormSubmitAttempted = signal(false);

  ngOnInit(): void {
    const timezone = this._timezoneData()?.find(
      (i) => i.id === this._installation()?.timezone?.id,
    );

    if (timezone) {
      this.selectedTimezone.set(timezone);

      this.deviceForm.patchValue(
        {
          timezone: timezone,
        },
        { emitEvent: false },
      );
    }
  }

  async dismiss(event: string): Promise<void> {
    this.modalController.dismiss(event);
  }

  async onSubmitDeviceAdd(): Promise<void> {
    this.isFormSubmitAttempted.set(true);

    if (this.deviceForm.valid) {
      this.addDeviceToInstallation(
        this._installationId(),
        this.deviceForm.value.serial_number ?? '',
        this.deviceForm.value.activation_code ?? '',
        this.deviceForm.value.timezone ?? ({ id: '', name: '' } as Timezone),
      );
    }
  }

  addDeviceToInstallation(
    id: number,
    serialNumber: string,
    activationKey: string,
    timezone: Timezone,
  ): void {
    const request: AddDeviceRequest = {
      serialNumber: serialNumber,
      activationKey: activationKey,
      timeZoneId: timezone.id,
    };

    this.installationsService
      .addDeviceToExistingInstallation(id, request)
      .subscribe({
        next: () => {
          this.feedbackService.present('device_added');
          this.dismiss('success');
        },
        error: (error) => {
          this.feedbackService.present(
            error.error.errorMessage ?? 'device_addFailed',
          );
        },
      });
  }
}
